import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Virtuoso } from 'react-virtuoso';
import {
  Heading,
  Avatar,
  Box,
  Tooltip,
  Divisor,
  Button,
  Spinner,
  Icon,
} from 'components';

import { routerStore } from 'core/routing';

// import mainTheme from 'styles/themes/wikifactory';
import { frameStore } from 'apps/components/Frame/appManager';
import { env } from 'process';
import { withNamespaces } from 'react-i18next';
import { requestNotificationPermission } from 'apps/utils';
import { InboxStyled, NoConversationsWrapper } from './inbox.styled';
import NoConversations from './icons/NoConversations';

import InboxItem from './InboxItem';

import BetaLabel from './BetaLabel';

type InboxProps = {
  onClose: () => void;
};

const buildFooter = (inboxManager: any) => {
  const VirtuosoFooter = withNamespaces()(
    observer(function VirtuosoFooter(props) {
      const handleLoadMore = () => {
        inboxManager.conversationsConnManager.fetchMore();
      };
      let content = null;
      if (inboxManager.loading) {
        content = <Spinner />;
      } else if (inboxManager.areThereMoreItemsToFetch) {
        content = (
          <Button
            text={props.t('Load More')}
            onClick={handleLoadMore}
            size='small'
            outline
          />
        );
      } else {
        content = <p>{props.t('- No more items to load -')}</p>;
      }
      return (
        <Box py={2} sx={{ display: 'flex', justifyContent: 'center' }}>
          {content}
        </Box>
      );
    }),
  );
  return VirtuosoFooter;
};

const Inbox = observer((props: InboxProps) => {
  const { onClose } = props;
  const { inboxManager } = frameStore;

  const sortedConversations = inboxManager.conversationsConnManager.nodes;

  const { hasDataBeenLoaded, total } = inboxManager.conversationsConnManager;
  const areThereNoConversations = hasDataBeenLoaded && total === 0;

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  const closeButton = (
    <Icon
      className='mr-4 cursor-pointer'
      data-testid='Inbox-closeButton'
      onClick={() => {
        onClose();
      }}
      name='close'
      size='small'
    />
  );

  const emptyContent = (
    <div>
      <div className='flex justify-end mt-3'>{closeButton}</div>
      <NoConversationsWrapper>
        <NoConversations />
        <h4 className='text-black'>No conversations yet.</h4>
      </NoConversationsWrapper>
    </div>
  );

  if (!total) {
    return emptyContent;
  }

  return (
    <InboxStyled>
      <div className='flex items-center justify-between'>
        <div className='flex items-center'>
          <Box px={3} py={2}>
            <Heading level={4} size='small'>
              Inbox
            </Heading>
          </Box>
          <BetaLabel />
        </div>
        {closeButton}
      </div>
      {/* <Box p={2} bg={mainTheme.color.wfGrey50}>
        <Input placeholder='Search' size='xsmall' icon='search' rounded />
      </Box> */}

      {/* TODO use old progress bar for now */}
      <Divisor />
      {areThereNoConversations ? (
        emptyContent
      ) : (
        <Virtuoso
          data={sortedConversations}
          endReached={() => {
            if (
              inboxManager.areThereMoreItemsToFetch &&
              !inboxManager.loading
            ) {
              inboxManager.conversationsConnManager.fetchMore();
            }
          }}
          components={{ Footer: buildFooter(inboxManager) }}
          itemContent={(index, conversation) => {
            const { context, type } = conversation;
            const { parentObj } = context;

            if (!conversation.lastMessage || !parentObj) {
              if (env.MODE === 'development')
                // eslint-disable-next-line no-console
                console.log(
                  'Inbox: Got a conversation with null lastMessage',
                  conversation,
                );
              return <div style={{ height: 2 }}></div>;
            }

            if (conversation.lastMessage.createdBy === null) {
              return <div style={{ height: 2 }}></div>;
            }

            const { profile } = conversation.lastMessage.createdBy;
            let fullName = '';

            if (profile) {
              ({ fullName } = profile);
            } else {
              fullName = 'Deleted user';
            }

            let itemName = conversation.resourceId;
            let tooltipText = '';
            let displayType = `${type}:`;

            let route;
            if (!conversation.uri) {
              route = { name: 'invalid_uri_empty', params: {} };
            } else {
              route = routerStore.router.matchUrl(conversation.uri);
              if (!route) {
                route = { name: 'invalid_uri_route_match', params: {} };
              }
            }

            const routeName = route.name;

            if (type === 'RFQ') {
              itemName = parentObj.title;
            } else if (type === 'FILE') {
              if (routeName === 'profile.shared.file') {
                // shared files
                displayType = 'File:';
                // TODO: Add the revision here. Not being returned
                itemName = `${parentObj.name}`;
              } else if (routeName === 'invalid_uri') {
                displayType = 'File with invalid URI';
                itemName = `${parentObj.name} ${conversation.uri}`;
              } else {
                // project files
                const projectName = parentObj.name;

                displayType = `${projectName} / File:`;
                tooltipText = conversation.resourceId;

                if (tooltipText) tooltipText = `${projectName}/${tooltipText}`;
                const lastSlashIndex = itemName.lastIndexOf('/');
                const fileName = itemName.substring(lastSlashIndex + 1);
                itemName = fileName;
              }
            } else if (type === 'ORDER') {
              displayType = 'Order:';
              itemName = parentObj.title;
            }

            let routeParams = route.params;
            if (!routeParams) routeParams = {}; // probably unnecessary
            routeParams.panel = 'discuss';

            const conversationTooltipId = `InboxConversationTooltip-${conversation.id}`;
            return (
              <>
                <InboxItem
                  key={conversation.id}
                  avatar={
                    <Avatar
                      name={fullName}
                      src={profile ? profile.avatar?.url : null}
                    />
                  }
                  type={displayType}
                  fileName={itemName}
                  userName={fullName}
                  body={conversation.lastMessage.snippet}
                  date={new Date(conversation.lastMessage.timestamp)}
                  unreadCount={conversation.unreadCount}
                  routeName={routeName}
                  routeParams={routeParams}
                  onClick={() => {
                    onClose();
                  }}
                  titleTooltip={tooltipText}
                  dataFor={conversationTooltipId}
                />
                <Tooltip delayShow={200} id={conversationTooltipId} />
              </>
            );
          }}
        ></Virtuoso>
      )}
    </InboxStyled>
  );
});

export default Inbox;
